<template>
  <div class="ips">
    <v-progress-linear v-if="!render" indeterminate color="primary" />
    <v-form v-if="render" ref="form" v-model="valid" class="ips__form" lazy-validation>
      <div class="ips__body">
        <section class="ips__card">
          <div class="ips__card-head">
            <v-icon small color="primary">mdi-format-title</v-icon>
            <span>{{ $t('invoiceSubtitle') }}</span>
          </div>
          <v-text-field
            v-model="invoiceSubtitle"
            :placeholder="$t('invoiceSubtitle')"
            :rules="[(v) => !!v || $t('is-required')]"
            hide-details="auto"
            outlined
            clearable
            dense
          />
        </section>

        <section class="ips__card">
          <div class="ips__card-head">
            <v-icon small color="primary">mdi-text-box-outline</v-icon>
            <span>{{ $t('settingInvoiceTerms') }}</span>
          </div>
          <div class="ips__editor">
            <tiptap-vuetify
              v-model="invoiceContent"
              :extensions="extensions"
              :toolbar-attributes="{ color: '#eef1f6' }"
            />
          </div>
        </section>

        <section class="ips__card">
          <div class="ips__card-head">
            <v-icon small color="primary">mdi-draw</v-icon>
            <span>{{ $t('settingSignatureStamp') }}</span>
          </div>
          <div class="ips__uploads">
            <div class="ips__upload" :class="{ 'ips__upload--on': companySignatureEnabled === 'active' }">
              <div class="ips__upload-top">
                <span class="ips__upload-title">{{ $t('settingCompanySignature') }}</span>
                <v-switch
                  v-model="companySignatureEnabled"
                  :true-value="'active'"
                  :false-value="'deactive'"
                  class="ips__switch"
                  color="primary"
                  inset
                  dense
                  hide-details
                />
              </div>
              <div class="ips__drop" @click="triggerSignatureUpload">
                <v-img
                  v-if="companySignaturePreviewUrl"
                  :src="companySignaturePreviewUrl"
                  max-height="64"
                  contain
                  class="ips__preview"
                />
                <template v-else>
                  <v-icon color="grey">mdi-cloud-upload-outline</v-icon>
                  <span class="ips__drop-text">{{ companySignatureFileName || $t('settingUploadSignature') }}</span>
                </template>
                <v-btn v-if="hasSignatureFile" class="ips__clear" icon x-small @click.stop="clearSignatureUpload">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
                <input ref="signatureInput" type="file" accept="image/*" class="ips__input" @change="onSignatureFile">
              </div>
            </div>

            <div class="ips__upload" :class="{ 'ips__upload--on': companyStampEnabled === 'active' }">
              <div class="ips__upload-top">
                <span class="ips__upload-title">{{ $t('settingCompanyStamp') }}</span>
                <v-switch
                  v-model="companyStampEnabled"
                  :true-value="'active'"
                  :false-value="'deactive'"
                  class="ips__switch"
                  color="primary"
                  inset
                  dense
                  hide-details
                />
              </div>
              <div class="ips__drop" @click="triggerStampUpload">
                <v-img
                  v-if="companyStampPreviewUrl"
                  :src="companyStampPreviewUrl"
                  max-height="64"
                  contain
                  class="ips__preview"
                />
                <template v-else>
                  <v-icon color="grey">mdi-cloud-upload-outline</v-icon>
                  <span class="ips__drop-text">{{ companyStampFileName || $t('settingUploadStamp') }}</span>
                </template>
                <v-btn v-if="hasStampFile" class="ips__clear" icon x-small @click.stop="clearStampUpload">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
                <input ref="stampInput" type="file" accept="image/*" class="ips__input" @change="onStampFile">
              </div>
            </div>
          </div>
        </section>
      </div>

      <div class="ips__foot">
        <v-btn color="primary" depressed class="app-form-action-btn ips__submit" :loading="isLoading" @click="onSubmit">
          <v-icon left small>mdi-content-save-outline</v-icon>{{ $t('submit') }}
        </v-btn>
      </div>
    </v-form>

    <v-snackbar
      v-model="snakbar"
      :color="snakbarColor ? snakbarColor : 'primary'"
      top
      style="z-index: 1000000000000 !important"
    >
      {{ snakbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn color="white" text v-bind="attrs" @click="snakbar = !snakbar">
          {{ $t('close') }}
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
// Settings copy of partial/invoicePrint.vue: script kept identical so the
// API payload ($data) and upload flow do not change; only the layout is new.
import General from "../../../../../Mixins/general";
import {
  // component
  TiptapVuetify,
  // extensions
  Heading,
  Bold,
  Italic,
  Strike,
  Underline,
  Code,
  Paragraph,
  BulletList,
  OrderedList,
  ListItem,
  Link,
  Blockquote,
  HardBreak,
  HorizontalRule,
  History,
  Image,
  Table,
  TableCell,
  TableHeader,
  TableRow,
} from "tiptap-vuetify";
export default {
  name: "InvoicePrintSettings",
  components: {
    TiptapVuetify,
  },
  mixins: [General],
  data: () => ({
    valid: true,
    extensions: [
      History,
      Table,
      TableCell,
      TableHeader,
      TableRow,
      Blockquote,
      Link,
      Underline,
      Strike,
      Italic,
      ListItem, // if you need to use a list (BulletList, OrderedList)
      BulletList,
      OrderedList,
      Image,
      [
        Heading,
        {
          // Options that fall into the tiptap's extension
          options: {
            levels: [1, 2, 3],
          },
        },
      ],
      Bold,
      Code,
      HorizontalRule,
      Paragraph,
      HardBreak, // line break on Shift + Ctrl + Enter
    ],
    invoiceSubtitle: null,
    invoiceContent: null,
    companySignatureEnabled: "deactive",
    companyStampEnabled: "deactive",
    companySignatureFile: null,
    companyStampFile: null,
    companySignatureFileName: null,
    companyStampFileName: null,
    companySignatureVal: null,
    companyStampVal: null,
    companySignaturePreviewUrl: null,
    companyStampPreviewUrl: null,
    paymentZibalSystem: null,
    paymentZibalAccountName: null,
    paymentZibalAccountShaba: null,
    paymentZibalConfirm: false,
    select: "invoicePrint",
    lists: [],
    render: false,
    isLoading: false,
  }),
  watch: {},
  activated() {
    this.show();
  },
  computed: {
    hasSignatureFile() {
      return !!(
        this.companySignatureFile ||
        this.companySignatureVal ||
        this.companySignatureFileName ||
        this.companySignaturePreviewUrl
      );
    },
    hasStampFile() {
      return !!(
        this.companyStampFile ||
        this.companyStampVal ||
        this.companyStampFileName ||
        this.companyStampPreviewUrl
      );
    },
  },
  beforeMount() {
    this.statusItems = [
      {
        value: "not_completed",
        text: this.$t("not_completed"),
      },
      {
        value: "canceled",
        text: this.$t("canceled"),
      },
      {
        value: "not_completed",
        text: this.$t("not_completed"),
      },
      {
        value: "unfulfilled",
        text: this.$t("unfulfilled"),
      },
      {
        value: "fulfilled",
        text: this.$t("fulfilled"),
      },
    ];
  },
  mounted() {
    this.show();
  },
  methods: {
    clearSignatureUpload() {
      if (
        this.companySignaturePreviewUrl &&
        this.companySignaturePreviewUrl.startsWith("blob:")
      ) {
        URL.revokeObjectURL(this.companySignaturePreviewUrl);
      }
      this.companySignatureFile = null;
      this.companySignatureFileName = null;
      this.companySignatureVal = null;
      this.companySignaturePreviewUrl = null;
      if (this.$refs.signatureInput) {
        this.$refs.signatureInput.value = "";
      }
    },
    clearStampUpload() {
      if (
        this.companyStampPreviewUrl &&
        this.companyStampPreviewUrl.startsWith("blob:")
      ) {
        URL.revokeObjectURL(this.companyStampPreviewUrl);
      }
      this.companyStampFile = null;
      this.companyStampFileName = null;
      this.companyStampVal = null;
      this.companyStampPreviewUrl = null;
      if (this.$refs.stampInput) {
        this.$refs.stampInput.value = "";
      }
    },
    addPaymentMethod() {
      this.paymentItems.push({
        bank: null,
        value: null,
      });
    },
    remove(index) {
      this.paymentItems.splice(index, 1);
    },
    triggerSignatureUpload() {
      this.$refs.signatureInput.click();
    },
    triggerStampUpload() {
      this.$refs.stampInput.click();
    },
    onSignatureFile(e) {
      const file = e.target.files && e.target.files[0];
      this.companySignatureFile = file || null;
      this.companySignatureFileName = file ? file.name : null;
      this.companySignaturePreviewUrl = file
        ? URL.createObjectURL(file)
        : this.companySignatureVal?.link || null;
    },
    onStampFile(e) {
      const file = e.target.files && e.target.files[0];
      this.companyStampFile = file || null;
      this.companyStampFileName = file ? file.name : null;
      this.companyStampPreviewUrl = file
        ? URL.createObjectURL(file)
        : this.companyStampVal?.link || null;
    },
    parseActiveDeactive(value) {
      if (value === "active" || value === true || value === 1 || value === "1") {
        return "active";
      }
      return "deactive";
    },
    getFileDisplayName(file) {
      if (!file) return null;
      if (typeof file === "string") return file;
      return file.title || file.name || file.fileName || null;
    },
    getFilePreviewUrl(file) {
      if (!file || typeof file === "string") return null;
      return file.link || file.url || null;
    },
    applyStoredUploadFile(file, kind) {
      if (!file) return;
      const name = this.getFileDisplayName(file);
      const preview = this.getFilePreviewUrl(file);
      if (kind === "signature") {
        this.companySignatureVal = file;
        this.companySignatureFileName = name;
        this.companySignaturePreviewUrl = preview;
      } else {
        this.companyStampVal = file;
        this.companyStampFileName = name;
        this.companyStampPreviewUrl = preview;
      }
    },
    resetUploadFields() {
      this.companySignatureFile = null;
      this.companyStampFile = null;
      this.companySignatureFileName = null;
      this.companyStampFileName = null;
      this.companySignatureVal = null;
      this.companyStampVal = null;
      this.companySignaturePreviewUrl = null;
      this.companyStampPreviewUrl = null;
    },
    normalizeInvoicePrintData(res) {
      if (!res) return null;
      if (
        res.data != null &&
        typeof res.data === "object" &&
        res.subtitle == null &&
        res.content == null
      ) {
        return res.data;
      }
      return res;
    },
    applyInvoicePrintResponse(res) {
      const data = this.normalizeInvoicePrintData(res);
      if (!data) return;

      this.invoiceSubtitle =
        data.subtitle != null
          ? data.subtitle
          : data.invoice_subtitle != null
          ? data.invoice_subtitle
          : data.invoiceSubtitle;
      this.invoiceContent =
        data.content != null
          ? data.content
          : data.invoice_content != null
          ? data.invoice_content
          : data.invoiceContent;

      const sigEnabled =
        data.companySignatureEnabled != null
          ? data.companySignatureEnabled
          : data.company_signature_enabled != null
          ? data.company_signature_enabled
          : data.signature_enabled;
      const stampEnabled =
        data.companyStampEnabled != null
          ? data.companyStampEnabled
          : data.company_stamp_enabled != null
          ? data.company_stamp_enabled
          : data.stamp_enabled;

      this.companySignatureEnabled = this.parseActiveDeactive(sigEnabled);
      this.companyStampEnabled = this.parseActiveDeactive(stampEnabled);

      const sigFile =
        data.signature ||
        data.companySignature ||
        data.company_signature ||
        data.signature_file ||
        data.signatureFile;
      const stampFile =
        data.stamp ||
        data.companyStamp ||
        data.company_stamp ||
        data.stamp_file ||
        data.stampFile;

      this.applyStoredUploadFile(sigFile, "signature");
      this.applyStoredUploadFile(stampFile, "stamp");

      if (Array.isArray(data.files)) {
        data.files.forEach((file) => {
          const type = (file.type || file.file_type || "").toLowerCase();
          if (type === "signature") this.applyStoredUploadFile(file, "signature");
          if (type === "stamp") this.applyStoredUploadFile(file, "stamp");
        });
      }
    },
    create() {
      if (!this.$refs.form.validate()) return false;
      var app = this;
      app.isLoading = true;
      return app.$axios
        .post(this.$endpoint + "setting", this.$data)
        .then((res) => {
          app.snakbarText = app.$t("registered");
          app.snakbarColor = "green";
          app.snakbar = true;
          return res;
        })
        .catch((err) => {
          this.serverErrorHandeler(err);
          return Promise.reject(err);
        });
    },
    onSubmit() {
      var app = this;
      const created = app.create();
      if (created === false) return false;
      created
        .then(() => app.uploadCompanySignatureAndStamp())
        .catch(() => {})
        .finally(() => {
          app.isLoading = false;
        });
    },
    buildSelectedFileMeta(file) {
      return {
        link: URL.createObjectURL(file),
        extension: ". " + (file.name.split(".")[1] || ""),
        title: file.name.split(".")[0] || "",
        _id: file.size + "" + file.lastModified,
        iconURL: "",
      };
    },
    uploadFileToCreatedetail(file, type) {
      const meta = this.buildSelectedFileMeta(file);
      const formData = new FormData();
      formData.append("files[0]", file);
      formData.append("selectedFile[0]", JSON.stringify(meta));
      formData.append("refId", "1");
      formData.append("type", type);
      formData.append("select", "fileUpload");
      return this.$axios.post(this.$endpoint + "createdetail", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    },
    uploadCompanySignatureAndStamp() {
      const app = this;
      const uploads = [];
      if (app.companySignatureEnabled === "active" && app.companySignatureFile) {
        uploads.push(app.uploadFileToCreatedetail(app.companySignatureFile, "signature"));
      }
      if (app.companyStampEnabled === "active" && app.companyStampFile) {
        uploads.push(app.uploadFileToCreatedetail(app.companyStampFile, "stamp"));
      }
      if (uploads.length === 0) {
        return Promise.resolve();
      }
      return Promise.all(uploads).catch((err) => {
        app.serverErrorHandeler(err);
        return Promise.reject(err);
      });
    },
    show() {
      var app = this;
      app.loading = true;
      const settingsUrl = this.$endpoint + "setting/" + 1 + "/edit";
      Promise.all([
        app.$axios.get(settingsUrl, { params: { select: app.select } }),
        app.$axios.get(settingsUrl),
      ])
        .then(([invoiceRes, generalRes]) => {
          app.resetUploadFields();
          app.applyInvoicePrintResponse({
            ...app.normalizeInvoicePrintData(generalRes.data),
            ...app.normalizeInvoicePrintData(invoiceRes.data),
          });
          app.$nextTick(() => {
            app.render = true;
          });
        })
        .catch((err) => {
          this.serverErrorHandeler(err);
        })
        .finally(() => (app.loading = false));
    },
    copied() {
      this.snakbarText = this.$t("copied");
      this.snakbarColor = "blue";
      this.snakbar = true;
    },
    // generatePDF () {
    //   const columns = [
    //     { title: 'ایسن', dataKey: 'title' },
    //     { title: 'Body', dataKey: 'body' },
    //   ]
    //   const doc = new Jspdf({
    //     orientation: 'portrait',
    //     unit: 'in',
    //     format: 'letter',
    //   })
    //   // doc.setFont('IRANSans', 'normal', 'bold')
    //   doc.setLanguage('ar')
    //   // text is placed using x, y coordinates
    //   doc.setFontSize(16).text(this.heading, 0.5, 1.0)
    //   // create a line under heading
    //   doc.setLineWidth(0.01).line(0.5, 1.1, 8.0, 1.1)
    //   // Using autoTable plugin
    //   autoTable(doc, {
    //     columns,
    //     body: this.items,
    //     margin: { left: 0.5, top: 1.25 },
    //   })
    //   // Using array of sentences
    //   doc
    //     .setFont('helvetica')
    //     .setFontSize(12)
    //     .text(this.moreText, 0.5, 3.5, { align: 'left', maxWidth: '7.5' })
    //
    //   // Creating footer and saving file
    //   doc.text(0, 10, 'Non-utf-8-string')
    //   doc
    //     // .setFont('times')
    //     // .setFontSize(11)
    //     // .setFontStyle('italic')
    //     // .setTextColor(0, 0, 255)
    //     // .text(
    //     //   'This is a simple footer located .5 inches from page bottom',
    //     //   0.5,
    //     //   doc.internal.pageSize.height - 0.5,
    //     // )
    //     .save(`${this.heading}.pdf`)
    // },
  },
};
</script>

<style scoped>
.ips {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}
.ips__form {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.ips__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.ips__body > * { flex-shrink: 0; }
.ips__card {
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 12px;
  padding: 14px 16px 16px;
  background: #fff;
}
.theme--dark .ips__card {
  background: rgba(255, 255, 255, 0.03);
  border-color: rgba(255, 255, 255, 0.12);
}
.ips__card-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 0.88rem;
  font-weight: 700;
}
.ips__card ::v-deep .v-input {
  margin: 0 !important;
  padding: 0 !important;
  height: auto !important;
}
.ips__editor {
  border: 1px solid rgba(15, 23, 42, 0.1);
  border-radius: 10px;
  overflow: hidden;
}
.ips__editor ::v-deep .tiptap-vuetify-editor .v-card {
  box-shadow: none !important;
  border-radius: 0 !important;
}
.ips__editor ::v-deep .tiptap-vuetify-editor__toolbar .v-toolbar {
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
}
.ips__editor ::v-deep .tiptap-vuetify-editor__content {
  min-height: 220px;
  max-height: 420px;
  overflow-y: auto;
  padding: 4px 8px;
}
.ips__uploads {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
}
.ips__upload {
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 12px;
  padding: 10px 12px 12px;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}
.ips__upload--on {
  border-color: rgba(25, 118, 210, 0.35);
  background: rgba(25, 118, 210, 0.03);
}
.ips__upload-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
}
.ips__upload-title {
  font-size: 0.86rem;
  font-weight: 600;
}
.ips__switch {
  margin: 0 !important;
  padding: 0 !important;
}
.ips__drop {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 96px;
  padding: 10px;
  border: 1.5px dashed rgba(15, 23, 42, 0.18);
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.02);
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}
.ips__drop:hover {
  border-color: var(--v-primary-base, #1976d2);
  background: rgba(25, 118, 210, 0.04);
}
.ips__drop-text {
  font-size: 0.78rem;
  opacity: 0.65;
  text-align: center;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ips__preview {
  max-width: 100%;
}
.ips__clear {
  position: absolute !important;
  top: 6px;
  inset-inline-end: 6px;
  background: #fff;
}
.ips__input {
  display: none;
}
.ips__foot {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 18px;
  border-top: 1px solid rgba(15, 23, 42, 0.07);
  background: #fafbfd;
}
.theme--dark .ips__foot {
  background: rgba(255, 255, 255, 0.03);
}

@media (max-width: 799px) {
  .ips__body { padding: 12px; }
  .ips__card { padding: 12px; }
  .ips__editor ::v-deep .tiptap-vuetify-editor__content { min-height: 180px; max-height: none; }
  .ips__foot { padding: 10px 12px calc(10px + env(safe-area-inset-bottom)); }
  .ips__submit { flex: 1 1 auto; height: 48px !important; border-radius: 12px !important; }
}
</style>
